import React, { useMemo } from 'react';
import { Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Rating, alpha } from '@mui/material';
import { grey } from '@mui/material/colors';
import { RealTimeLocation, RealTimeEVSE, TariffElement } from '@domain/graphql.types';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ReferenceLine, CartesianGrid, Cell } from 'recharts';

type ChipsProps = {
  siteId: number | string;
  rtl: RealTimeLocation;
};

type UtilProps = {
  siteId: number | string;
  rtl: RealTimeLocation;
  title?: React.ReactNode;
};

type TableProps = {
  siteId: number | string;
  rtl: RealTimeLocation;
};

/* -------------------- helpers (scoped to this file) -------------------- */

function minutesSinceMidnightInTZ(now: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const hh = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const mm = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return hh * 60 + mm;
}

function parseHHMM(s: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) throw new Error(`Invalid time "${s}" (expected HH:MM)`);
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) {
    throw new Error(`Out-of-range time "${s}"`);
  }
  return hh * 60 + mm;
}

function isTariffElementActiveNow(t: TariffElement, timeZone: string, now = new Date()): boolean {
  const r = t.restrictions ?? null;

  const hasStart = !!r?.startTime;
  const hasEnd = !!r?.endTime;
  if (!r || (!hasStart && !hasEnd)) return true;

  const startMin = r?.startTime ? parseHHMM(r.startTime) : 0;
  const endMin = r?.endTime ? parseHHMM(r.endTime) : 23 * 60 + 59;

  if (hasStart && hasEnd && startMin === endMin) return true;
  const nowMin = minutesSinceMidnightInTZ(now, timeZone);

  if (startMin <= endMin) return nowMin >= startMin && nowMin <= endMin;
  return nowMin >= startMin || nowMin <= endMin;
}

function avgReliabilityScore(chargingPool: RealTimeLocation['chargingPool'] | undefined | null): number {
  if (!chargingPool || chargingPool.length === 0) return 0;
  const scores = chargingPool.map((cp) => cp.reliabilityScore ?? 0).filter((s) => typeof s === 'number');
  if (scores.length === 0) return 0;
  return scores.reduce((a, b) => a + (b || 0), 0) / scores.length;
}

function extractPricing(chargingPool: RealTimeLocation['chargingPool'] | undefined | null, timeZone = 'UTC') {
  const blank = { pricing: '-', pricingNumeric: 0, idleFee: '-', flatFee: '-' };
  if (!chargingPool || chargingPool.length === 0) return blank;

  const withPricing = chargingPool.filter((cp) => !!cp.currentPricing);
  if (withPricing.length === 0) return blank;

  const pricingBucket = withPricing[0].currentPricing ?? {};
  const bucket = (pricingBucket as any)['non-member'] ?? (pricingBucket as any).member;
  const elements: TariffElement[] = Array.isArray(bucket?.elements) ? bucket?.elements : [];
  if (elements.length === 0) return blank;

  const findElem = (predicate: (e: TariffElement) => boolean) => elements.find(predicate);

  const baseElem = findElem(
    (e) =>
      !Object.hasOwn(e.restrictions ?? {}, 'vehicleRequestingPower') &&
      isTariffElementActiveNow(e, timeZone) &&
      e.priceComponents.some((pc) => pc.type === 'ENERGY' || pc.type === 'TIME'),
  );

  let pricing = '-';
  let pricingNumeric = 0;

  if (baseElem) {
    const energy = baseElem.priceComponents.find((pc) => pc.type === 'ENERGY');
    const time = baseElem.priceComponents.find((pc) => pc.type === 'TIME');
    if (energy) {
      pricingNumeric = energy.price ?? 0;
      pricing = `${energy.price}/kWh`;
    } else if (time) {
      pricingNumeric = time.price ?? 0;
      pricing = `${time.price}/h`;
    }
  }

  const idleElem = findElem(
    (e) => !!e.restrictions?.vehicleRequestingPower && isTariffElementActiveNow(e, timeZone) && e.priceComponents.some((pc) => pc.type === 'TIME'),
  );
  const idlePc = idleElem?.priceComponents.find((pc) => pc.type === 'TIME');
  const idleFee = idlePc ? `${idlePc.price}/h` : '-';

  const flatElem = findElem(
    (e) =>
      !Object.hasOwn(e.restrictions ?? {}, 'vehicleRequestingPower') &&
      isTariffElementActiveNow(e, timeZone) &&
      e.priceComponents.some((pc) => pc.type === 'FLAT'),
  );
  const flatPc = flatElem?.priceComponents.find((pc) => pc.type === 'FLAT');
  const flatFee = flatPc ? String(flatPc.price) : '-';

  return { pricing, pricingNumeric, idleFee, flatFee };
}

function connectorsFromEVSEs(evses: RealTimeEVSE[] | undefined): string[] {
  if (!Array.isArray(evses)) return [];
  const set = new Set<string>();
  evses.forEach((evse) => evse.connectors?.forEach((c) => c?.standard && set.add(c.standard)));
  return Array.from(set);
}

function connectorTypeFromEVSEs(evses: RealTimeEVSE[] | undefined): string {
  const connectorType = '—';
  const distinctConnectorTypes = Array.from(new Set(evses?.flatMap((e) => e.connectors?.map((c) => c.standard)))).sort();
  return distinctConnectorTypes.length > 0 ? distinctConnectorTypes.join(', ') : connectorType;
}

function maxPowerFromEVSEs(evses: RealTimeEVSE[] | undefined): number | undefined {
  if (!Array.isArray(evses) || evses.length === 0) return undefined;
  let max = 0;
  evses.forEach((evse) => {
    evse.connectors?.forEach((c) => {
      const val = parseInt(String(c.maxPower), 10);
      if (!Number.isNaN(val)) max = Math.max(max, val);
    });
  });
  return max || undefined;
}

function waitTimeFromEVSEs(evses: RealTimeEVSE[] | undefined): { available: number; total: number; waitMinutes?: number; unknownWait?: boolean } {
  if (!Array.isArray(evses)) return { available: 0, total: 0, unknownWait: true };
  const total = evses.length;
  const available = evses.filter((e) => e.status === 'AVAILABLE').length;

  if (available > 0) return { available, total, waitMinutes: 0 };

  const charging = evses.filter((e) => e.status === 'CHARGING' && typeof e.estimatedWaitTime === 'number');
  if (charging.length === 0) return { available, total, unknownWait: true };

  const minSec = charging.reduce((min, e) => Math.min(min, e.estimatedWaitTime as number), Number.POSITIVE_INFINITY);
  const minutes = Math.max(0, Math.round((minSec || 0) / 60));
  return { available, total, waitMinutes: minutes };
}

const renderStars = (score?: number | null) => {
  if (score == null) return '—';
  const stars = Math.min(5, Math.max(1, Math.ceil(score / 20)));
  return <Rating value={stars} max={5} readOnly size="small" />;
};

const successfulText = (evses: RealTimeEVSE[] | undefined): string => {
  if (!evses || evses.length === 0) return '—';
  const anyCharging = evses.some((e) => (e.status ?? '').toUpperCase() === 'CHARGING');
  if (anyCharging) return 'Used now';

  let latest: Date | null = null;
  for (const e of evses) {
    const ts = e.lastSuccessfulCharge;
    if (!ts) continue;
    const d = new Date(ts as unknown as string);
    if (!Number.isNaN(d.getTime()) && (!latest || d > latest)) latest = d;
  }
  if (!latest) return '—';

  const now = new Date();
  const diffMs = now.getTime() - latest.getTime();
  if (diffMs < 0) return '—';

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  if (latest >= startOfToday) {
    const mins = Math.floor(diffMs / 60000);
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hours = Math.floor(mins / 60);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

/** Merge hourly_historical_utilization across EVSEs to a 24-length array of [0..1] */
function buildHourlyUtilization(evses: RealTimeEVSE[] | undefined): number[] {
  const count = Array.isArray(evses) && evses.length > 0 ? evses.length : 1;
  const totals = Array(24).fill(0);
  (evses || []).forEach((evse) => {
    const hh = evse?.metrics?.hourlyHistoricalUtilization as Record<string, { chargingSeconds?: number }> | undefined;
    if (!hh) return;
    for (let h = 0; h < 24; h++) {
      const rec = (hh as any)[h] ?? hh[String(h)];
      const secs = rec?.chargingSeconds || 0;
      totals[h] += secs;
    }
  });
  const hourlyUtilization = totals.map((secs) => {
    const ratio = secs / (count * 3600);
    return Math.max(0, Math.min(1, ratio || 0));
  });
  return hourlyUtilization;
}

/* -------------------- Components -------------------- */

export function RealTimeLocationChips({ rtl }: ChipsProps) {
  const evses = useMemo(() => (rtl?.chargingPool ?? []).flatMap((p) => p.evses ?? []), [rtl]);
  const connectors = useMemo(() => connectorsFromEVSEs(evses), [evses]);
  const { available, total, waitMinutes, unknownWait } = useMemo(() => waitTimeFromEVSEs(evses), [evses]);
  const reliability = useMemo(() => avgReliabilityScore(rtl?.chargingPool), [rtl]);
  const pricing = useMemo(() => extractPricing(rtl?.chargingPool, rtl?.timezone), [rtl]);

  const reliabilityColor = reliability === 0 ? grey[100] : reliability < 80 ? '#FF0000' : reliability < 90 ? '#FF8C00' : '#228B22';

  return (
    <>
      <Chip
        size="small"
        sx={{ borderRadius: 0.5, fontWeight: 'medium', fontSize: 13, height: 20, '&>span': { paddingX: '4px' } }}
        label={`Avail: ${available}/${total}`}
      />
      <Chip
        size="small"
        sx={{ borderRadius: 0.5, fontWeight: 'medium', fontSize: 13, height: 20, '&>span': { paddingX: '4px' } }}
        label={`Wait: ${available > 0 ? 'None' : unknownWait ? 'Unknown' : `${waitMinutes} min`}`}
      />
      {connectors.length > 0 && (
        <Chip
          size="small"
          sx={{ borderRadius: 0.5, fontWeight: 'medium', fontSize: 13, height: 20, '&>span': { paddingX: '4px' } }}
          label={connectors.join(', ')}
        />
      )}
      <Chip
        size="small"
        sx={{ borderRadius: 0.5, fontWeight: 'medium', fontSize: 13, height: 20, '&>span': { paddingX: '4px' } }}
        label={`$${pricing.pricing}`}
      />
      {pricing.idleFee !== '-' && (
        <Chip
          size="small"
          sx={{ borderRadius: 0.5, fontWeight: 'medium', fontSize: 13, height: 20, '&>span': { paddingX: '4px' } }}
          label={`Idle: $${pricing.idleFee}`}
        />
      )}
      {pricing.flatFee !== '-' && (
        <Chip
          size="small"
          sx={{ borderRadius: 0.5, fontWeight: 'medium', fontSize: 13, height: 20, '&>span': { paddingX: '4px' } }}
          label={`Flat: $${pricing.flatFee}`}
        />
      )}
      <Chip
        size="small"
        sx={{
          borderRadius: 0.5,
          fontWeight: 'medium',
          fontSize: 13,
          height: 20,
          '&>span': { paddingX: '4px' },
          color: reliabilityColor,
          border: `1px solid ${alpha(reliabilityColor, 0.4)}`,
        }}
        label={`Reliability: ${reliability === 0 ? '—' : reliability.toFixed(0)}`}
      />
    </>
  );
}

export function RealTimeLocationUtilization({ siteId, rtl, title = <strong style={{ paddingLeft: '10%', fontSize: 15 }}>Utilization</strong> }: UtilProps) {
  const evses = useMemo(() => (rtl?.chargingPool ?? []).flatMap((p) => p.evses ?? []), [rtl]);
  const hourly = useMemo(() => buildHourlyUtilization(evses), [evses]);

  if (!hourly || hourly.length !== 24) return null;

  const data = hourly.map((v, i) => ({ hour: i, label: String(i), utilization: Math.round(v * 100), ratio: v }));

  return (
    <div style={{ marginBottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', height: 150, marginTop: 4 }}>
      {title}
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" ticks={['0', '6', '12', '18', '23']} interval={0} />
          <YAxis domain={[0, 100]} tickFormatter={(t) => `${t}%`} width={40} />
          <ReTooltip formatter={(value: any) => [`${value}%`, 'Utilization']} labelFormatter={(l) => `Hour ${l}`} />
          <ReferenceLine y={50} stroke="#ddd" />
          <Bar dataKey="utilization" isAnimationActive={false} radius={[3, 3, 0, 0]}>
            {data.map((d) => {
              const hue = Math.round((1 - d.ratio) * 120);
              const fill = `hsl(${hue} 70% 45%)`;
              return <Cell key={`util-cell-${siteId}-${d.hour}`} fill={fill} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RealTimeLocationStationsTable({ rtl }: TableProps) {
  if (!Array.isArray(rtl?.chargingPool) || rtl.chargingPool.length === 0) return null;

  return (
    <TableContainer component={Paper} elevation={0} sx={{ mt: 1, border: '1px solid', borderColor: 'divider' }}>
      <Table size="small" aria-label="Charging stations">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Power</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Reliability</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Successful</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rtl.chargingPool?.map((st, i) => (
            <TableRow key={st.id ?? i}>
              <TableCell>{String(st.id)}</TableCell>
              <TableCell>{maxPowerFromEVSEs(st.evses)} Kw</TableCell>
              <TableCell>
                <Tooltip title={st.reliabilityScore != null ? `${Math.round(st.reliabilityScore)}` : ''}>
                  <span>{renderStars(st.reliabilityScore)}</span>
                </Tooltip>
              </TableCell>
              <TableCell>{connectorTypeFromEVSEs(st.evses) || '—'}</TableCell>
              <TableCell>{successfulText(st.evses)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
